import { fmtNum, fmtPct } from '../utils/fmt';
import { useState, useEffect, useMemo, useRef } from 'react';
import { fmtPrice } from '../utils/format';
import { fetchWithCache } from '../utils/fetchWithCache';
import InfoTooltip from './InfoTooltip';
import './CalculatorTab.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Megjelenítés: utils/fmt (magyar formátum); a „$” a szám után kerül
function fmtUSD(n, dec = 2) {
  if (n == null || isNaN(n)) return '—';
  return fmtNum(n, dec);
}

function leverageRisk(lev) {
  if (lev <= 3)   return { icon: '🟢', label: 'Konzervatív', color: '#00c853' };
  if (lev <= 10)  return { icon: '🟡', label: 'Mérsékelt',    color: '#f5a623' };
  if (lev <= 25)  return { icon: '🟠', label: 'Agresszív',    color: '#ff9800' };
  if (lev <= 50)  return { icon: '🔴', label: 'Veszélyes',    color: '#ff4d4d' };
  return               { icon: '💀', label: 'Extrém',        color: '#c00'    };
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div className="cc-field">
      <label className="cc-label">{label}</label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, prefix, suffix, placeholder, step, min = '0' }) {
  return (
    <div className="cc-input-wrap">
      {prefix && <span className="cc-affix">{prefix}</span>}
      <input
        className="cc-input"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={e => onChange(e.target.value.replace(',', '.'))}
        placeholder={placeholder ?? '0'}
        step={step}
        min={min}
      />
      {suffix && <span className="cc-affix cc-affix--r">{suffix}</span>}
    </div>
  );
}

function DirToggle({ value, onChange }) {
  return (
    <div className="cc-dir-toggle">
      <button className={`cc-dir-btn cc-dir-btn--l ${value === 'long'  ? 'active' : ''}`} onClick={() => onChange('long')}>▲ Long</button>
      <button className={`cc-dir-btn cc-dir-btn--s ${value === 'short' ? 'active' : ''}`} onClick={() => onChange('short')}>▼ Short</button>
    </div>
  );
}

function ResultBox({ rows, sentiment, warning }) {
  return (
    <div className={`cc-result-box cc-result-box--${sentiment}`}>
      {warning && <div className="cc-result-warning">{warning}</div>}
      {rows.map(({ label, value, big, cls }) => (
        <div key={label} className={`cc-result-row ${big ? 'big' : ''}`}>
          <span className="cc-result-label">{label}</span>
          <span className={`cc-result-value ${cls ?? ''}`}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Position Size Calculator ──────────────────────────────────────────────────

function PositionSizeCalc({ livePrice, coinKey, coinSymbol }) {
  const prevKey = useRef(null);
  const [dir,     setDir]     = useState('long');
  const [account, setAccount] = useState('10000');
  const [riskPct, setRiskPct] = useState('1');
  const [entry,   setEntry]   = useState(livePrice != null ? String(livePrice) : '');
  const [stop,    setStop]    = useState('');
  const [stopMode, setStopMode] = useState('pct');
  const [stopPct,  setStopPct]  = useState('2');

  useEffect(() => {
    if (coinKey !== prevKey.current) {
      prevKey.current = coinKey;
      if (livePrice != null) setEntry(String(livePrice));
    }
  }, [coinKey, livePrice]);

  // Stop price derived from % distance when in '% Távolság' mode
  const eNum = parseFloat(entry);
  const effStopPrice = stopMode === 'pct' && eNum && parseFloat(stopPct)
    ? (dir === 'long' ? eNum * (1 - parseFloat(stopPct) / 100) : eNum * (1 + parseFloat(stopPct) / 100))
    : null;

  const res = useMemo(() => {
    const acc = parseFloat(account);
    const risk = parseFloat(riskPct);
    const e = parseFloat(entry);
    const s = stopMode === 'pct'
      ? (e && parseFloat(stopPct)
          ? (dir === 'long' ? e * (1 - parseFloat(stopPct) / 100) : e * (1 + parseFloat(stopPct) / 100))
          : NaN)
      : parseFloat(stop);
    if (!acc || !risk || !e || !s || e === s) return null;
    const validDir = dir === 'long' ? s < e : s > e;
    const riskUSD  = acc * (risk / 100);
    const dist     = Math.abs(e - s);
    const distPct  = (dist / e) * 100;
    const posUSD   = riskUSD / (dist / e);
    const posCoins = posUSD / e;
    const leverage = posUSD / acc;
    return { validDir, riskUSD, distPct, posUSD, posCoins, leverage };
  }, [dir, account, riskPct, entry, stop, stopMode, stopPct]);

  const sentiment = !res ? 'neutral'
    : !res.validDir ? 'negative'
    : res.leverage > 10 ? 'warning'
    : 'positive';

  return (
    <div className="cc-card cc-card--green cc-card--full">
      <div className="cc-card-head">
        <div className="cc-card-title">📐 Pozíció Méret</div>
        <div className="cc-card-sub">Kockázat alapú pozícióméret számítás</div>
      </div>

      <div className="cc-fields">
        <Field label="Irány"><DirToggle value={dir} onChange={setDir} /></Field>
        <div className="cc-fields-2col">
          <Field label="Számla Méret">
            <NumInput value={account} onChange={setAccount} prefix="$" placeholder="10000" />
          </Field>
          <Field label={<>Kockázat / Trade <InfoTooltip text="Az összes tőkéd hány százalékát kockáztatod egy trade-en. Profi kereskedők általában max 1-2%-ot kockáztatnak — így 50 vesztes trade után is van tőkéd folytatni." /></>}>
            <NumInput value={riskPct} onChange={setRiskPct} suffix="%" placeholder="1" step="0.1" />
          </Field>
          <Field label="Belépési Ár">
            <NumInput value={entry} onChange={setEntry} prefix="$" placeholder={livePrice ?? '0'} />
          </Field>
          <Field label={
            <span className="cc-sl-labelrow">
              <span>Stop Loss{stopMode === 'usd' && <> <InfoTooltip text="A stop ára dollárban." /></>}</span>
              <span className="cc-sl-toggle">
                <button type="button" className={`cc-sl-mode ${stopMode === 'usd' ? 'active' : ''}`} onClick={() => setStopMode('usd')}>$ Ár</button>
                <button type="button" className={`cc-sl-mode ${stopMode === 'pct' ? 'active' : ''}`} onClick={() => setStopMode('pct')}>% Távolság</button>
              </span>
            </span>
          }>
            {stopMode === 'pct' ? (
              <>
                <NumInput value={stopPct} onChange={setStopPct} suffix="%" placeholder="2" step="0.1" />
                {effStopPrice != null && <div className="cc-sl-hint">= {fmtPrice(effStopPrice)}</div>}
              </>
            ) : (
              <NumInput value={stop} onChange={setStop} prefix="$" placeholder="0" />
            )}
          </Field>
        </div>
      </div>

      {res ? (
        <ResultBox
          sentiment={sentiment}
          warning={
            !res.validDir
              ? `⚠ Stop Loss ${dir === 'long' ? 'long-nál belépési ár alatt' : 'short-nál belépési ár felett'} kell legyen!`
              : res.leverage > 10 ? '⚠ Magas tőkeáttétel!'
              : null
          }
          rows={[
            { label: 'Kockáztatott összeg',      value: `${fmtUSD(res.riskUSD)} $` },
            { label: 'Pozíció méret (USD)',        value: `${fmtUSD(res.posUSD)} $`,   big: true },
            { label: `Pozíció méret (${coinSymbol})`, value: `${fmtNum(res.posCoins, 6)} ${coinSymbol}`, big: true },
            { label: 'Stop Loss távolság',         value: `${fmtNum(res.distPct, 2)}%` },
            { label: <span>Szükséges tőkeáttétel <InfoTooltip text="Mennyi tőkeáttétel szükséges a pozícióhoz a számlád méretéhez képest. 1x alatt nincs tőkeáttétel. 10x felett már magas kockázat — kis ármozdulás is nagy veszteséget okozhat." /></span>, value: `${fmtNum(res.leverage, 1)}×` },
          ]}
        />
      ) : (
        <div className="cc-empty">Töltsd ki az összes mezőt a számításhoz</div>
      )}
    </div>
  );
}

// ── PnL Calculator ────────────────────────────────────────────────────────────

function PnLCalc({ livePrice, coinKey }) {
  const prevKey = useRef(null);
  const [dir,      setDir]      = useState('long');
  const [entry,    setEntry]    = useState(livePrice != null ? String(livePrice) : '');
  const [exit_,    setExit]     = useState('');
  const [size,     setSize]     = useState('');
  const [leverage, setLeverage] = useState('1');

  useEffect(() => {
    if (coinKey !== prevKey.current) {
      prevKey.current = coinKey;
      if (livePrice != null) setEntry(String(livePrice));
    }
  }, [coinKey, livePrice]);

  const res = useMemo(() => {
    const e   = parseFloat(entry);
    const x   = parseFloat(exit_);
    const s   = parseFloat(size);
    const lev = parseFloat(leverage) || 1;
    if (!e || !x || !s) return null;
    const rawPct = ((x - e) / e) * 100;
    const pnlPct = dir === 'long' ? rawPct : -rawPct;
    const pnlUSD = s * (pnlPct / 100);
    const roePct = pnlPct * lev;
    return { pnlPct, pnlUSD, roePct };
  }, [dir, entry, exit_, size, leverage]);

  const sentiment = !res ? 'neutral' : res.pnlUSD >= 0 ? 'positive' : 'negative';

  return (
    <div className="cc-card cc-card--blue">
      <div className="cc-card-head">
        <div className="cc-card-title">💰 Nyereség/Veszteség</div>
        <div className="cc-card-sub">Zárt trade eredményének számítása</div>
      </div>

      <div className="cc-fields">
        <Field label="Irány"><DirToggle value={dir} onChange={setDir} /></Field>
        <div className="cc-fields-2col">
          <Field label="Belépési Ár">
            <NumInput value={entry} onChange={setEntry} prefix="$" placeholder={livePrice ?? '0'} />
          </Field>
          <Field label="Kilépési Ár">
            <NumInput value={exit_} onChange={setExit} prefix="$" placeholder="0" />
          </Field>
          <Field label="Pozíció Méret">
            <NumInput value={size} onChange={setSize} prefix="$" placeholder="1000" />
          </Field>
          <Field label={<span>Tőkeáttétel <InfoTooltip text="A ROE (tőkén felüli hozam) mutatja a tényleges hozamodat a befektetett tőkédre vetítve. 10x tőkeáttételnél egy 5%-os ármozdulás 50% ROE-t jelent — de veszteség esetén ugyanígy felerősödik." /></span>}>
            <NumInput value={leverage} onChange={setLeverage} suffix="×" placeholder="1" step="1" min="1" />
          </Field>
        </div>
      </div>

      {res ? (
        <ResultBox
          sentiment={sentiment}
          rows={[
            {
              label: 'Bruttó P&L',
              value: <span className={res.pnlUSD >= 0 ? 'positive' : 'negative'} style={{ fontSize: 18 }}>
                {res.pnlUSD >= 0 ? '+' : '−'}{fmtUSD(Math.abs(res.pnlUSD))} $
              </span>,
              big: true,
            },
            {
              label: '% nyereség',
              value: `${fmtPct(res.pnlPct, 2)}`,
              cls: res.pnlPct >= 0 ? 'positive' : 'negative',
            },
            {
              label: 'Tőkén felüli hozam (ROE)',
              value: `${fmtPct(res.roePct, 2)}`,
              cls: res.roePct >= 0 ? 'positive' : 'negative',
            },
          ]}
        />
      ) : (
        <div className="cc-empty">Töltsd ki az összes mezőt a számításhoz</div>
      )}
    </div>
  );
}

// ── Liquidation Calculator ────────────────────────────────────────────────────

function LiqCalc({ livePrice, coinKey }) {
  const prevKey = useRef(null);
  const [dir,    setDir]    = useState('long');
  const [entry,  setEntry]  = useState(livePrice != null ? String(livePrice) : '');
  const [lev,    setLev]    = useState(10);
  const [margin, setMargin] = useState('0.5');

  useEffect(() => {
    if (coinKey !== prevKey.current) {
      prevKey.current = coinKey;
      if (livePrice != null) setEntry(String(livePrice));
    }
  }, [coinKey, livePrice]);

  const res = useMemo(() => {
    const e    = parseFloat(entry);
    const maint = parseFloat(margin) / 100;
    if (!e || lev <= 1) return null;
    const liqLong  = e * (1 - 1 / lev + maint);
    const liqShort = e * (1 + 1 / lev - maint);
    const liq      = dir === 'long' ? liqLong : liqShort;
    const distPct  = ((liq - e) / e) * 100;
    return { liq, distPct };
  }, [dir, entry, lev, margin]);

  const risk = leverageRisk(lev);

  return (
    <div className="cc-card cc-card--red">
      <div className="cc-card-head">
        <div className="cc-card-title">💀 Likvidációs Ár <InfoTooltip text="Az az árszint, ahol a tőzsdéd automatikusan lezárja a pozíciódat mert a veszteség elérte a befektetett tőkédet. Ha az ár ide ér, mindent elveszítesz a pozícióból." /></div>
        <div className="cc-card-sub">Becsült likvidációs szint tőkeáttételhez</div>
      </div>

      <div className="cc-fields">
        <Field label="Irány"><DirToggle value={dir} onChange={setDir} /></Field>
        <Field label="Belépési Ár">
          <NumInput value={entry} onChange={setEntry} prefix="$" placeholder={livePrice ?? '0'} />
        </Field>

        <Field label={<>Tőkeáttétel — <span style={{ color: risk.color }}>{risk.icon} {risk.label}</span> <InfoTooltip text="Tőkeáttétellel többet nyerhetsz, de ugyanannyival többet is veszíthetsz. 10x tőkeáttételnél egy 10%-os árcsökkenés 100%-os veszteséget jelent — a teljes tőkédet elveszíted (likvidáció)." /></>}>
          <div className="cc-slider-row">
            <input
              type="range" min="1" max="100" value={lev}
              onChange={e => setLev(Number(e.target.value))}
              className="cc-slider"
              style={{ '--lev-pct': `${(lev - 1) / 99 * 100}%` }}
            />
            <span className="cc-slider-num" style={{ color: risk.color }}>{lev}×</span>
          </div>
          <div className="cc-slider-ticks">
            <span>1×</span><span>25×</span><span>50×</span><span>75×</span><span>100×</span>
          </div>
        </Field>

        <Field label="Fenntartási Margin">
          <NumInput value={margin} onChange={setMargin} suffix="%" placeholder="0.5" step="0.1" />
        </Field>
      </div>

      {res ? (
        <ResultBox
          sentiment="negative"
          rows={[
            {
              label: dir === 'long' ? 'Long Likvidációs Ár' : 'Short Likvidációs Ár',
              value: fmtPrice(res.liq),
              big: true,
              cls: 'negative',
            },
            {
              label: 'Távolság belépéstől',
              value: `${fmtPct(res.distPct, 2)}`,
              cls: res.distPct < 0 ? 'negative' : 'positive',
            },
            {
              label: dir === 'long'
                ? `Ha az ár ${fmtPrice(res.liq)}-re esik, likvid`
                : `Ha az ár ${fmtPrice(res.liq)}-re emelkedik, likvid`,
              value: '⚠',
              cls: 'negative',
            },
          ]}
        />
      ) : (
        <div className="cc-empty">Töltsd ki az összes mezőt a számításhoz</div>
      )}
      <div className="cc-disclaimer">
        ⚠ Becslés — tényleges likvidáció a tőzsde díjaitól és funding rátától is függ.
      </div>
    </div>
  );
}

// ── Funding Cost Calculator ───────────────────────────────────────────────────

function FundingCalc() {
  const [dir,       setDir]       = useState('long');
  const [size,      setSize]      = useState('1000');
  const [rate,      setRate]      = useState('0.0100');
  const [liveLoaded, setLiveLoaded] = useState(false);

  useEffect(() => {
    fetchWithCache('https://api.bybit.com/v5/market/tickers?category=linear&symbol=BTCUSDT', { cacheDuration: 5 * 60 * 1000 })
      .then(j => {
        const raw = j.result?.list?.[0]?.fundingRate;
        if (raw) {
          setRate((parseFloat(raw) * 100).toFixed(4));
          setLiveLoaded(true);
        }
      })
      .catch(() => {});
  }, []);

  const res = useMemo(() => {
    const s = parseFloat(size);
    const r = parseFloat(rate) / 100;
    if (!s || isNaN(r)) return null;
    const pays = dir === 'long' ? r > 0 : r < 0;
    const per8h  = s * Math.abs(r);
    const daily  = per8h * 3;
    const weekly = daily * 7;
    const monthly = daily * 30;
    const breakEvenPct = Math.abs(r) * 3 * 100;
    return { pays, per8h, daily, weekly, monthly, breakEvenPct };
  }, [dir, size, rate]);

  return (
    <div className="cc-card cc-card--purple">
      <div className="cc-card-head">
        <div className="cc-card-title">
          ⏱ Funding Költség Kalkulátor
          {liveLoaded && <span className="cc-live-badge">● Élő BTC ráta</span>}
        </div>
        <div className="cc-card-sub">Mennyi funding rátát fizetsz a pozíciódon?</div>
      </div>

      <div className="cc-fields-3col">
        <Field label="Irány"><DirToggle value={dir} onChange={setDir} /></Field>
        <Field label="Pozíció Méret">
          <NumInput value={size} onChange={setSize} prefix="$" placeholder="1000" />
        </Field>
        <Field label={<span>Funding Ráta (8 óránként) <InfoTooltip text="A futures piacon 8 óránként a longosok fizetnek a shortosoknak (vagy fordítva), attól függően melyik oldal dominál. Pozitív ráta = longok fizetnek. Negatív ráta = shortok fizetnek." /></span>}>
          <NumInput value={rate} onChange={setRate} suffix="%" placeholder="0.0100" step="0.0001" />
        </Field>
      </div>

      {res ? (
        <div className="cc-funding-results">
          {[
            { label: '8 óránként',     val: res.per8h   },
            { label: 'Naponta (3×)',   val: res.daily   },
            { label: 'Hetente',        val: res.weekly  },
            { label: 'Havonta',        val: res.monthly },
          ].map(({ label, val }) => (
            <div key={label} className={`cc-fund-row ${res.pays ? 'negative' : 'positive'}`}>
              <span className="cc-fund-label">{label}</span>
              <strong className="cc-fund-val">
                {res.pays ? '−' : '+'}{fmtUSD(val)} $
              </strong>
            </div>
          ))}
          <div className="cc-fund-row cc-fund-row--be">
            <span className="cc-fund-label">Nullszaldós ármozgás (naponta)</span>
            <strong className="cc-fund-val" style={{ color: 'var(--accent)' }}>
              {fmtNum(res.breakEvenPct, 4)}%
            </strong>
          </div>
          <div className="cc-fund-note">
            {res.pays
              ? `${dir === 'long' ? 'Long' : 'Short'} pozíción funding rátát fizetsz.`
              : `${dir === 'long' ? 'Long' : 'Short'} pozíción funding rátát kapsz! 🎯`}
            {' '}A funding ráta 8 óránként változik.
          </div>
        </div>
      ) : (
        <div className="cc-empty">Töltsd ki az összes mezőt a számításhoz</div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function CalculatorTab({ selectedCoin, coins, onSelectCoin }) {
  // One selected coin for the whole terminal (App.selectedCoin): the dropdown
  // here switches it, and a watchlist / search pick switches this tab too.
  const [localCoinId, setLocalCoinId] = useState(selectedCoin?.id ?? coins?.[0]?.id ?? '');
  const calcCoinId = onSelectCoin ? (selectedCoin?.id ?? coins?.[0]?.id ?? '') : localCoinId;
  const setCalcCoinId = (id) => {
    if (!onSelectCoin) { setLocalCoinId(id); return; }
    const c = coins?.find(x => x.id === id);
    if (c) onSelectCoin(c);
  };

  const calcCoin   = useMemo(() => coins?.find(c => c.id === calcCoinId) ?? selectedCoin ?? null, [coins, calcCoinId, selectedCoin]);
  const livePrice  = calcCoin?.current_price ?? null;
  const coinSymbol = calcCoin?.symbol?.toUpperCase() ?? '—';
  const coinKey    = calcCoin?.id ?? '';

  return (
    <div className="calc-tab">

      {/* ── Coin selector bar ── */}
      {coins?.length > 0 && (
        <div className="cc-coin-bar">
          <span className="cc-coin-bar-label">Coin kiválasztása:</span>
          <select
            className="cc-coin-select"
            value={calcCoinId}
            onChange={e => setCalcCoinId(e.target.value)}
          >
            {coins.map(c => (
              <option key={c.id} value={c.id}>{c.symbol.toUpperCase()} — {c.name}</option>
            ))}
          </select>
          {livePrice != null && (
            <span className="cc-live-price">
              <span className="cc-live-dot">●</span>
              {fmtPrice(livePrice)}
            </span>
          )}
        </div>
      )}

      {/* ── Two-column grid ── */}
      <div className="cc-grid">
        <div className="cc-col-l">
          <PositionSizeCalc livePrice={livePrice} coinKey={coinKey} coinSymbol={coinSymbol} />
        </div>
        <div className="cc-col-r">
          <PnLCalc livePrice={livePrice} coinKey={coinKey} />
          <LiqCalc livePrice={livePrice} coinKey={coinKey} />
        </div>
      </div>

      {/* ── Full-width funding section ── */}
      <div className="cc-funding-section">
        <FundingCalc />
      </div>
    </div>
  );
}
